<template>
  <main id="mainContent" ref="mainContentRef">
    <!-- <div> -->
    <NotificationStatus :show-message="displayNotification" />
 
    <div class="app-main-content">
      <p class="printShow">
        {{ message }}
      </p>
      <div class="app-content-panels">
        <div class="app-panel-left">
          <BaseButtonGroup>
            <button type="button" @click="handleRouting('info')">Resend confirmation</button>
            <button type="button" class="app-btn-primary" @click="handleRouting('print')">Print confirmation and receipt</button>
          </BaseButtonGroup>

          <ServiceInfo :show-barcode="false" />
          <PaymentReceipt />

          <BaseButtonGroup>
            <button class="button-home" type="button" @click="handleRouting('home')">
              Back to customer profile page
            </button>
          </BaseButtonGroup>
        </div>
        <AsideForUser />
      </div>

      <ModalContact :show-modal="showContactForm" @doClose="showContactForm = false"
                    @doNotificationMessage="handleModalNotification" />

    </div>
  </main>
</template>


<script setup>
import { ref, computed, onMounted, nextTick, watch } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import ServiceInfo from '../../components/confirmation/ServiceInfo.vue';
import PaymentReceipt from '../../components/confirmation/PaymentReceipt.vue';
import BaseButtonGroup from '../../components/ui/BaseButtonGroup.vue';
import AsideForUser from '../../components/confirmation/AsideForUser.vue';
import ModalContact from '../../components/confirmation/ModalContact.vue';
import NotificationStatus from '../../components/confirmation/NotificationStatus.vue';
import * as COMMONS from '../../store/_commons';
import appConfig from '@/app.config';
import { useHead } from '@unhead/vue'; 

defineOptions({
  name: 'Confirmation',
});

// Use the useHead composable to manage page metadata
useHead({
  title: `Transactions receipt | ${appConfig.shortName}`,
  meta: [
    {
      name: 'description',
      content: 'Print or save your transaction(s) receipt'
    }
  ]
});

// Vuex store and Vue Router
const store = useStore();
const router = useRouter();

// Reactive data
const screenData = ref({});
const showContactForm = ref(false);
const displayNotification = ref(true);
const modalCaller = ref('getContact');
const inWorkTxnMessage = ref([]);
const message = ref('');
const mainContentRef = ref(null);

// Computed properties
const STEPS = computed(() => store.state.STEPS);

const isBusinessUser = computed(() => store.state.guestLogin.isBusinessUser);

// Profile route based on user type
const profileRoute = computed(() => {
  return isBusinessUser.value ? 'BusinessProfile' : 'Profile';
});

const registrationInfo = computed(() => store.getters[COMMONS.GUEST_REGISTRATION_INFO]);
const adjudicationLicenseInfo = computed(() => store.getters[COMMONS.GUEST_ADJUDICATION_LICENSE_INFO]);
const adjudicationCommercialLicenseInfo = computed(() => store.getters[COMMONS.GUEST_ADJUDICATION_COMMERCIAL_LICENSE_INFO]);
const adjudicationRegistrationInfo = computed(() => store.getters[COMMONS.GUEST_ADJUDICATION_REGISTRATION_INFO]);

const businessRegistrationInfo = computed(() => store.getters[COMMONS.BUSINESS_REGISTRATION_INFO]);
const businessAdjudicationRegistrationInfo = computed(() => store.getters[COMMONS.GUEST_BUSINESS_ADJUDICATION_REGISTRATION_INFO]);

// Watcher for showContactForm
watch(showContactForm, (newValue) => {
  if (!newValue) {
    const caller = document.getElementById(modalCaller.value);
    if (caller) {
      nextTick(() => caller.focus());
    }
  }
});

// Methods
const handleModalNotification = (value) => {
  displayNotification.value = true;
  store.state.guestLogin.resNotificationSent = value;
};

const handleRouting = (target) => {
  switch (target) {
    case 'info':
      getContactInfo();
      break;
    case 'print':
      printConfirmation();
      break;
    case 'home':
      clearTransactionState();
      router.push({ name: profileRoute.value });
      break;
    default:
      // nada
  }
};

const clearTransactionState = () => {
  // Reset step and cart
  store.state.guestLogin.step = 1;
  store.state.guestLogin.renewalCart = [];
  store.state.guestLogin.cartTransactions = null;
  store.state.guestLogin.resNotificationSent = null;
  store.state.guestLogin.finalizationCart = null;
  store.state.guestLogin.totalBill = null;

  if (isBusinessUser.value) {
    // Clear business user data
    businessRegistrationInfo.value.businessRegistrationInformation = null;
    businessAdjudicationRegistrationInfo.value.businessAdjudicationRegistrationInformation = null;
  } else {
    // Clear individual user data
    registrationInfo.value.registrationInformation = null;
    adjudicationLicenseInfo.value.adjudicationLicenseInformation = null;
    adjudicationCommercialLicenseInfo.value.adjudicationCommercialLicenseInformation = null;
    adjudicationRegistrationInfo.value.adjudicationRegistrationInformation = null;
  }
};

const getContactInfo = () => {
  displayNotification.value = false;
  showContactForm.value = false;
  nextTick(() => {
    showContactForm.value = true;
  });
};

const printConfirmation = () => {
  window.print();
};

const checkInWorkTxns = () => {
  for(let i in store.state.guestLogin.finalizationCart.finalizeCartResponse.transactionCart.cartTransactions) {
    if( store.state.guestLogin.finalizationCart.finalizeCartResponse.transactionCart.cartTransactions[i].transactionData.transactionStatus == "In Work") {  
      inWorkTxnMessage.value.push(store.state.guestLogin.finalizationCart.finalizeCartResponse.transactionCart.cartTransactions[i].transactionData.transactionId)
    }
  }
  if(inWorkTxnMessage.value.length!==0) {
    message.value="Following Transaction(s) have failed. Transaction ID: " + inWorkTxnMessage.value.join(",",inWorkTxnMessage.value) + ". For more information, please contact the DMV at 401-462-4368. For transaction(s) completed successfully a confirmation text and / or e-mail has been sent to the information provided.";
  }
  else
  {
    message.value="Following RI DMV Online Customer Account transaction(s) have completed successfully. Below are the transaction and payment details."
  }
  
};

// Lifecycle hooks
onMounted(() => {
  window.scroll(0, 0);
  screenData.value = STEPS.value[1];
  checkInWorkTxns();

  nextTick(() => {
    if (mainContentRef.value) {
      mainContentRef.value.focus();
    } 
    else {
      document.body.setAttribute('tabindex', '1');
      document.body.focus();
      document.body.removeAttribute('tabindex');
    }
  });
});
</script>


<style lang="scss" scoped>
.app-panel-left :deep(.button-group) {
  padding-bottom: calc(var(--padding-vertical) * .75);
}
.printShow{
  display: none;
}
@media print{
  .printShow {
    display: block; 
  }
}
button {
  margin-bottom: 1.125em;
  &:not(:last-child) {
    margin-right: 1em;
  }
  &.disabled {
    cursor: not-allowed;
    opacity: 0.7;
  }
}
</style>